/**
 * The agent's composer shortcuts, loaded once and shared.
 *
 * They belong to the signed-in agent rather than to an app, so this sits
 * outside the workspace context: switching tenant must not reload them, and
 * editing one in a dialog has to be visible in the composer behind it.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api.js';

const ShortcutsContext = createContext(null);

export function ShortcutsProvider({ children }) {
  const [shortcuts, setShortcuts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    api.auth.shortcuts
      .list()
      .then((list) => live && setShortcuts(Array.isArray(list) ? list : []))
      .catch(() => live && setShortcuts([]))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, []);

  // Each mutation answers with the whole list, so there is nothing to merge —
  // and no way for the composer to disagree with the editor about what exists.
  const create = useCallback(
    async (shortcut) => setShortcuts(await api.auth.shortcuts.create(shortcut)),
    []
  );
  const update = useCallback(
    async (id, patch) => setShortcuts(await api.auth.shortcuts.update(id, patch)),
    []
  );
  const remove = useCallback(async (id) => setShortcuts(await api.auth.shortcuts.remove(id)), []);
  const restoreDefaults = useCallback(
    async () => setShortcuts(await api.auth.shortcuts.restoreDefaults()),
    []
  );

  const value = useMemo(
    () => ({ shortcuts, loading, create, update, remove, restoreDefaults }),
    [shortcuts, loading, create, update, remove, restoreDefaults]
  );

  return <ShortcutsContext.Provider value={value}>{children}</ShortcutsContext.Provider>;
}

export function useShortcuts() {
  const context = useContext(ShortcutsContext);
  if (!context) throw new Error('useShortcuts must be used inside <ShortcutsProvider>');
  return context;
}

/**
 * What the composer should offer for the text currently in the box.
 *
 * Only a lone `/word` at the very start counts — mid-sentence a slash is a
 * slash (`and/or`, a URL), and hijacking it there would be worse than not
 * having the feature.
 */
export function matchShortcuts(shortcuts, value) {
  const match = /^\/([a-z0-9-]*)$/i.exec(value);
  if (!match) return { query: null, matches: [] };
  const query = match[1].toLowerCase();
  return {
    query,
    matches: shortcuts.filter((s) => s.key.startsWith(query)),
  };
}

export default ShortcutsProvider;
